<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Alert from '../components/Alert.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import Code from '../components/Code.svelte';
  import Async from '../components/Async.svelte';
  import { getSources, getSource, saveSource } from '../lib/api.js';
  import { parseAndProfileCsv } from '../../../lib/disclosure_core.js';
  import { SELF } from '../../shared/config.js';
  import { HELP } from '../lib/help.js';
  import { nav, go } from '../lib/nav.svelte.js';

  let form = $state({ id: '', label: '', fileName: '', mediaType: '', csvText: '', sourceKey: '', timestampColumn: '' });
  let sourceId = $state(nav.params.source ?? null);
  let sourceChosen = $state(Boolean(nav.params.source));
  let creating = $state(false);
  async function load() {
    const { sources } = await getSources();
    if (!sources.length) return { sources, connector: null, mapping: null };
    if (!sourceId || !sources.some((source) => source.id === sourceId)) {
      sourceId = sources[0].id;
      sourceChosen = sources.length === 1;
    }
    const { connector, mapping } = await getSource(sourceId);
    if (!form.fileName) { form.id = sourceId; form.label = connector.sourceLabel ?? connector.label; }
    return { sources, connector, mapping };
  }
  let reloadKey = $state(0);
  const data = $derived.by(() => { reloadKey; return load(); });
  let profile = $state(null);
  let error = $state(null);
  let saved = $state(null);
  let saving = $state(false);

  async function choose(event) {
    error = null; saved = null;
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('CSV exceeds the 5 MB limit.');
      const csvText = await file.text();
      profile = parseAndProfileCsv(csvText);
      form.fileName = file.name; form.mediaType = file.type || 'text/csv'; form.csvText = csvText;
      form.sourceKey ||= '@row';
      form.timestampColumn ||= profile.headers.find((h) => /date|time|created/i.test(h)) ?? profile.headers[0] ?? '';
    } catch (e) { profile = null; error = e.message; }
  }

  async function save() {
    saving = true; error = null; saved = null;
    try { saved = await saveSource(form); reloadKey += 1; }
    catch (e) { error = e.message; }
    finally { saving = false; }
  }
</script>

<h1 class="wf2-title wf2-title-level-1">Source connector</h1>
<p class="wf2-subtitle">Stage a CSV under a stable managed reference, then author what its columns mean.</p>

<Async promise={data} loading="Reading connector config from your Pod…">
  {#snippet children({ sources, connector, mapping })}
    {#if connector}
      <label class="wf2-field">Configured source
        <select class="wf2-control" bind:value={sourceId} onchange={() => { sourceChosen = true; creating = false; profile = null; form.fileName = ''; go('connector', { source: sourceId }); reloadKey += 1; }}>
          {#each sources as source}<option value={source.id}>{source.label}</option>{/each}
        </select>
      </label>
      {@const sourceColumns = mapping.columns.map((m) => m.source).filter((s) => !s.startsWith('@'))}
      <HelpDrawer {...HELP.connector}><Code lang="json" text={JSON.stringify(connector, null, 2)} /></HelpDrawer>
      <Section title="Source"><Panel>
        <DataTable columns={['Field', 'Value']} rows={[
          ['Connector', connector.id], ['Label', connector.sourceLabel ?? connector.label],
          ['Source', connector.source?.descriptor ?? connector.source], ['Key column', connector.sourceKey],
          ['Timestamp column', connector.timestampColumn || 'none — snapshot source'],
          ['Record containers', Object.values(mapping.records).map((r) => SELF.podBase + r.container).join(', ')],
          ['Last run / check', connector.lastRun ? new Date(connector.lastRun).toISOString() : 'never'],
        ]}/>
        <p class="wf2-hint">Replacing this source requires the same ID, stages the new file, and resets its mapping and watermark. Existing private records are not published or deleted; matching keys are overwritten on re-import.</p>
      </Panel></Section>
      <Section title="Mapped columns"><Panel><Code lang="text" text={sourceColumns.join('\n') || '(none labelled yet)'} /></Panel></Section>
    {:else}
      <Alert title="Import your first dataset" variant="info"><p>No source is configured. The CSV remains private and ingest does not publish it.</p></Alert>
    {/if}

    {#if connector && !creating}<Button variant="secondary" onclick={() => { creating = true; profile = null; form = { id: '', label: '', fileName: '', mediaType: '', csvText: '', sourceKey: '', timestampColumn: '' }; }}>Add another source</Button>{/if}
    <Section title={creating || !connector ? 'Add source' : 'Replace selected source CSV'}><Panel>
      <label class="wf2-field">CSV file <input class="wf2-control" type="file" accept=".csv,text/csv" onchange={choose} /></label>
      {#if profile}
        <p class="wf2-hint">{form.fileName} · {profile.rows.length} rows · {profile.headers.length} columns</p>
        <div class="wf2-row" style="gap:12px">
          <label class="wf2-field">Source ID <input class="wf2-control" bind:value={form.id} readonly={!creating && Boolean(connector)} placeholder="ticket-sales" /></label>
          <label class="wf2-field">Label <input class="wf2-control" bind:value={form.label} placeholder="Ticket sales export" /></label>
        </div>
        <div class="wf2-row" style="gap:12px">
          <label class="wf2-field">Record ID <select class="wf2-control" bind:value={form.sourceKey}><option value="@row">Generated row number</option>{#each profile.headers as h}<option value={h}>{h}</option>{/each}</select></label>
          <label class="wf2-field">Timestamp column <select class="wf2-control" bind:value={form.timestampColumn}><option value="">None (snapshot source)</option>{#each profile.headers as h}<option value={h}>{h}</option>{/each}</select></label>
        </div>
        {#if form.sourceKey === '@row'}<p class="wf2-hint">Generated IDs follow CSV row order. Keep that order stable when replacing the file so existing records are overwritten rather than duplicated.</p>{/if}
        <DataTable columns={['Column', 'Filled', 'Distinct', 'Examples']} rows={profile.profiles.map((p) => [p.name, `${p.nonEmpty}`, `${p.distinct}`, p.examples.join(', ')])}/>
        <Button variant="primary" onclick={save} disabled={saving || (connector && !creating && !sourceChosen)}>{saving ? 'Importing…' : creating || !connector ? 'Create source and mapping' : 'Replace source and reset mapping'}</Button>
        {#if connector && !creating && !sourceChosen}<p class="wf2-hint">Choose the source explicitly before replacing it.</p>{/if}
      {/if}
      {#if error}<Alert title="Could not import CSV" variant="danger"><p>{error}</p></Alert>{/if}
      {#if saved}<Alert title="Source imported" variant="success"><p>{saved.source.rows} rows staged. Sign in as the analyst and open Sources to label columns before ingest.</p></Alert>{/if}
    </Panel></Section>
    <div class="wf2-row" style="gap:12px"><Button variant="secondary" to="dashboard">Back to dashboard</Button></div>
  {/snippet}
</Async>
